/**
 * termsVersion.js — single source of truth for the version string we
 * record when a user clicks-accepts the Terms of Service and Privacy
 * Policy at sign-in.
 *
 * Bump this string whenever EITHER document is materially changed.
 * The stored `userPrefs.termsAcceptedVersion` becomes "older than current"
 * for existing users; we can use that later to re-prompt at sign-in.
 *
 * Walter 2026-05-15: introduced alongside the click-accept Terms flow
 * (Phase 1 polish item 2). Pre-existing users carry no recorded version
 * — their consent is implied by continued use under the prior Terms.
 */
export const TERMS_VERSION = '2026-05-15';

const PENDING_KEY = 'lists_pending_terms_consent';

/**
 * Called from LoginPage when the user clicks any sign-in button with
 * the agreement checkbox checked. Stashes the version + timestamp in
 * localStorage so the post-auth flusher in App.js can write it to
 * Firestore once user.uid is available (sign-in is async, especially
 * the OAuth-redirect and magic-link-roundtrip paths).
 */
export function recordPendingConsent() {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({
      version: TERMS_VERSION,
      at: Date.now(),
    }));
  } catch {
    // localStorage unavailable (private mode, quota) — silently skip.
    // The continued-use legal fallback still applies in this case.
  }
}

/**
 * Reads + clears the pending consent record. Called from the App.js
 * post-auth effect once user.uid is in hand. Returns null if there's
 * nothing to flush.
 */
export function consumePendingConsent() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}
